import React, { useEffect, useMemo, useRef, useState } from 'react';
import downArrow from '../../assets/images/down-arrow.svg';

export interface VerticalOption {
  id: number;
  key?: string;
  name: string;
  description?: string | null;
}

interface VerticalMultiSelectProps {
  label?: string;
  verticals: VerticalOption[];
  selectedVerticalIds: number[];
  onChange: (verticalIds: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
}

const VerticalMultiSelect: React.FC<VerticalMultiSelectProps> = ({
  label,
  verticals,
  selectedVerticalIds,
  onChange,
  placeholder = 'Select verticals',
  disabled = false,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current || containerRef.current.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const selectedVerticals = useMemo(() => {
    if (!selectedVerticalIds.length) {
      return [];
    }

    const selectedSet = new Set(selectedVerticalIds);
    return verticals.filter((vertical) => selectedSet.has(vertical.id));
  }, [selectedVerticalIds, verticals]);

  const buttonLabel = useMemo(() => {
    if (isLoading) {
      return 'Loading verticals...';
    }

    if (!selectedVerticals.length) {
      return placeholder;
    }

    if (selectedVerticals.length <= 2) {
      return selectedVerticals.map((vertical) => vertical.name).join(', ');
    }

    const [first, second] = selectedVerticals;
    const remainingCount = selectedVerticals.length - 2;
    return `${first.name}, ${second.name} +${remainingCount} more`;
  }, [isLoading, placeholder, selectedVerticals]);

  const handleToggle = (verticalId: number) => {
    if (selectedVerticalIds.includes(verticalId)) {
      onChange(selectedVerticalIds.filter((id) => id !== verticalId));
    } else {
      onChange([...selectedVerticalIds, verticalId]);
    }
  };

  return (
    <div className="flex flex-col gap-1" ref={containerRef}>
      {label && <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</label>}
      <div className="relative">
        <button
          type="button"
          className="focus:ring-blue-primary flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setIsOpen((prev) => !prev)}
          disabled={disabled || isLoading}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="truncate">{buttonLabel}</span>
          <img src={downArrow} alt="" className={`ml-2 h-2 w-2 transform transition ${isOpen ? '' : 'rotate-180'}`} />
        </button>
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
              ) : verticals.length ? (
                verticals.map((vertical) => {
                  const isChecked = selectedVerticalIds.includes(vertical.id);
                  return (
                    <label
                      key={vertical.id}
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        className="text-blue-primary focus:ring-blue-primary h-4 w-4 rounded border-gray-300"
                        checked={isChecked}
                        onChange={() => handleToggle(vertical.id)}
                      />
                      <div>
                        <div className="font-medium">{vertical.name}</div>
                        {vertical.description && <div className="text-xs text-gray-500">{vertical.description}</div>}
                      </div>
                    </label>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">No verticals available.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerticalMultiSelect;
